import React, { useState, useEffect } from 'react';
import BadgeAvatar from '../components/BadgeAvatar';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import UserBadge from '../components/UserBadge';
import { calculateLevel } from '../utils/badgeSystem';

const Student = ({ user, logout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allCaseStudies, setAllCaseStudies] = useState([]);
  const [allComments, setAllComments] = useState([]);

  useEffect(() => {
    fetchStudentData();
    if (user?.is_moderator) {
      fetchAllCaseStudies();
      fetchAllComments();
    }
  }, []);

  const fetchStudentData = async () => {
    try {
      const response = await api.get('/users/me/student-dashboard');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching student data:', error);
      toast.error('Failed to load student dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCaseStudies = async () => {
    try {
      const response = await api.get('/case-studies');
      setAllCaseStudies(response.data);
    } catch (error) {
      console.error('Error fetching case studies:', error);
      toast.error('Failed to load case studies');
    }
  };

  const fetchAllComments = async () => {
    try {
      const response = await api.get('/admin/comments');
      setAllComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
    }
  };

  const deleteCaseStudy = async (caseStudyId) => {
    if (!window.confirm('Are you sure you want to delete this case study?')) return;
    try {
      await api.delete(`/case-studies/${caseStudyId}`);
      toast.success('Case study deleted successfully');
      fetchAllCaseStudies();
    } catch (error) {
      console.error('Error deleting case study:', error);
      toast.error('Failed to delete case study');
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    try {
      await api.delete(`/case-studies/comments/${commentId}`);
      toast.success('Comment deleted successfully');
      fetchAllComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
        <div className='text-center'>
          <div className='inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600'></div>
          <p className='mt-4 text-teal-600 font-medium'>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      <header className='bg-white shadow-sm border-b border-gray-200'>
        <div className='container mx-auto px-4 py-4'>
          <div className='flex items-center justify-between'>
            <Link to='/dashboard' className='flex items-center space-x-3'>
              <div className='w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center'>
                <svg className='w-6 h-6 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' />
                </svg>
              </div>
              <span className='text-xl font-bold text-gray-900' style={{ fontFamily: 'Playfair Display, serif' }}>TCM Study Club</span>
            </Link>
            <div className='flex items-center space-x-4'>
              <Link to='/dashboard'>
                <Button variant='outline' className='border-gray-300 text-gray-700 hover:bg-gray-100 rounded-full px-6'>Back to Dashboard</Button>
              </Link>
              <Link to={`/profile/${user.id}`}>
                <img src={user.picture || 'https://via.placeholder.com/40'} alt={user.name} className='w-10 h-10 rounded-full border-2 border-teal-500' />
              </Link>
              <Button onClick={logout} variant='outline' className='border-gray-300 text-gray-700 hover:bg-gray-100 rounded-full px-6'>Logout</Button>
            </div>
          </div>
        </div>
      </header>

      <div className='container mx-auto px-4 py-8 max-w-6xl'>
        <div className='bg-white rounded-3xl p-8 shadow-lg'>
          <div className='mb-8'>
            <div className='flex items-center space-x-3 mb-2'>
              <h1 className='text-4xl font-bold text-gray-900' style={{ fontFamily: 'Playfair Display, serif' }}>Student Dashboard</h1>
              {user?.is_moderator && <Badge className='bg-blue-100 text-blue-700 border-0 text-lg'>🛡️ Moderator</Badge>}
            </div>
            <p className='text-gray-600'>Track your learning progress and achievements</p>
          </div>

          {/* Badge Progress Section */}
          <div className='bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 rounded-2xl p-8 mb-8 border-2 border-purple-200'>
            <div className='flex items-start justify-between flex-wrap gap-6'>
              <div className='flex-1 min-w-[300px]'>
                <h2 className='text-2xl font-bold text-gray-900 mb-4 flex items-center'>
                  <span className='mr-2'>🏆</span> Your Level & Badge
                </h2>
                <UserBadge postCount={user.comment_count || 0} size="xl" showProgress={true} />
                <p className='text-sm text-gray-600 mt-4 italic'>
                  Keep contributing to discussions to level up and unlock exclusive badges!
                </p>
              </div>
              <div className='bg-white rounded-xl p-6 shadow-md max-w-md'>
                <h3 className='font-bold text-gray-900 mb-3'>📈 How to Level Up:</h3>
                <ul className='space-y-2 text-sm text-gray-700'>
                  <li className='flex items-start'>
                    <span className='text-purple-600 mr-2'>✓</span>
                    <span>Share your diagnosis and treatment ideas</span>
                  </li>
                  <li className='flex items-start'>
                    <span className='text-purple-600 mr-2'>✓</span>
                    <span>Discuss acupuncture point selections</span>
                  </li>
                  <li className='flex items-start'>
                    <span className='text-purple-600 mr-2'>✓</span>
                    <span>Engage with peer comments</span>
                  </li>
                  <li className='flex items-start'>
                    <span className='text-purple-600 mr-2'>✓</span>
                    <span>Earn new badges every 10 levels!</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-8'>
            <div className='bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-200'>
              <div className='text-3xl font-bold text-teal-600'>{data?.stats?.total_case_studies || 0}</div>
              <div className='text-sm text-gray-600 mt-1'>Case Studies Posted</div>
            </div>
            <div className='bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200'>
              <div className='text-3xl font-bold text-blue-600'>{data?.stats?.total_tests_taken || 0}</div>
              <div className='text-sm text-gray-600 mt-1'>Tests Taken</div>
            </div>
            <div className='bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200'>
              <div className='text-3xl font-bold text-green-600'>{data?.stats?.average_score || 0}%</div>
              <div className='text-sm text-gray-600 mt-1'>Average Score</div>
            </div>
            <div className='bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200'>
              <div className='text-3xl font-bold text-purple-600'>{data?.stats?.completed_cases || 0}</div>
              <div className='text-sm text-gray-600 mt-1'>Cases Completed</div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue='tests' className='w-full'>
            <TabsList className={`grid w-full ${user?.is_moderator ? 'grid-cols-3' : 'grid-cols-2'} mb-8 bg-gray-100 p-1 rounded-xl`}>
              <TabsTrigger value='tests' className='rounded-lg'>Test History</TabsTrigger>
              <TabsTrigger value='cases' className='rounded-lg'>My Case Studies</TabsTrigger>
              {user?.is_moderator && <TabsTrigger value='moderation' className='rounded-lg'>🛡️ Moderation</TabsTrigger>}
            </TabsList>

            <TabsContent value='tests'>
              <div className='space-y-4'>
                <div className='flex items-center justify-between mb-6'>
                  <h3 className='text-xl font-bold text-gray-900'>Test History</h3>
                  <div className='text-sm text-gray-600'>
                    Simple: {data?.stats?.simple_tests || 0} | Complex: {data?.stats?.complex_tests || 0}
                  </div>
                </div>

                {!data?.test_history || data.test_history.length === 0 ? (
                  <div className='text-center py-12'>
                    <svg className='w-16 h-16 mx-auto text-gray-400 mb-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
                    </svg>
                    <p className='text-gray-600 mb-4'>No tests taken yet</p>
                    <div className='flex gap-3 justify-center'>
                      <Link to='/test/simple'>
                        <Button className='difficulty-simple border-0'>Take Simple Test</Button>
                      </Link>
                      <Link to='/test/complex'>
                        <Button className='difficulty-complex border-0'>Take Complex Test</Button>
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className='space-y-3'>
                    {data.test_history.map((test, index) => (
                      <div key={test.id || index} className='bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-all'>
                        <div className='flex items-center justify-between'>
                          <div className='flex-1'>
                            <div className='flex items-center space-x-3 mb-2'>
                              <Badge className={`${test.difficulty === 'simple' ? 'difficulty-simple' : 'difficulty-complex'} border-0 text-xs`}>
                                {test.difficulty === 'simple' ? '📘 Simple' : '📕 Complex'}
                              </Badge>
                              <span className='text-sm text-gray-600'>{formatDate(test.submitted_at)}</span>
                            </div>
                            <div className='text-xs text-gray-500'>Time taken: {test.time_taken}s</div>
                          </div>
                          <div className='text-right'>
                            <div className={`text-2xl font-bold ${
                              test.score >= 80 ? 'text-green-600' : 
                              test.score >= 60 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {test.score}%
                            </div>
                            <div className='text-xs text-gray-500'>
                              {test.score >= 80 ? 'Excellent' : test.score >= 60 ? 'Good' : 'Needs Work'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value='cases'>
              <div className='space-y-4'>
                <div className='flex items-center justify-between mb-6'>
                  <h3 className='text-xl font-bold text-gray-900'>My Case Studies</h3>
                  {!user?.is_moderator && (user?.is_admin || user?.is_study_master) && (
                    <Link to='/create'>
                      <Button className='bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-full'>Create New</Button>
                    </Link>
                  )}
                </div>

                {!data?.my_case_studies || data.my_case_studies.length === 0 ? (
                  <div className='text-center py-12'>
                    <svg className='w-16 h-16 mx-auto text-gray-400 mb-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 6v6m0 0v6m0-6h6m-6 0H6' />
                    </svg>
                    <p className='text-gray-600 mb-4'>No case studies posted yet</p>
                    {!user?.is_moderator && (user?.is_admin || user?.is_study_master) && (
                      <Link to='/create'>
                        <Button className='bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-full'>Create Your First Case Study</Button>
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className='space-y-3'>
                    {data.my_case_studies.map(cs => (
                      <Link key={cs.id} to={`/case-study/${cs.id}`}>
                        <div className='bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-all'>
                          <div className='flex items-start justify-between'>
                            <div className='flex-1'>
                              <div className='flex items-center space-x-2 mb-2'>
                                <h4 className='font-bold text-gray-900'>{cs.title}</h4>
                                <Badge className={`${cs.difficulty === 'simple' ? 'difficulty-simple' : 'difficulty-complex'} border-0 text-xs`}>
                                  {cs.difficulty}
                                </Badge>
                              </div>
                              <p className='text-sm text-gray-600 mb-2 line-clamp-2'>{cs.description}</p>
                              <div className='flex items-center space-x-4 text-xs text-gray-500'>
                                <span>👁 {cs.view_count} views</span>
                                <span>👍 {cs.upvotes} upvotes</span>
                                <span>{formatDate(cs.created_at)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Moderation Tab - Only visible to moderators */}
            {user?.is_moderator && (
              <TabsContent value='moderation'>
                <div className='space-y-6'>
                  {/* Case Studies Moderation */}
                  <div>
                    <h3 className='text-xl font-bold text-gray-900 mb-4'>Case Studies Moderation</h3>
                    <div className='text-sm text-gray-600 mb-4'>
                      Total: {allCaseStudies.length} case studies | You can edit or delete any case study
                    </div>
                    {allCaseStudies.length === 0 ? (
                      <div className='text-center py-8 text-gray-500'>No case studies found</div>
                    ) : (
                      <div className='space-y-3'>
                        {allCaseStudies.map(cs => (
                          <div key={cs.id} className='bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-all'>
                            <div className='flex items-start justify-between'>
                              <div className='flex-1'>
                                <div className='flex items-center space-x-2 mb-2'>
                                  <h4 className='font-bold text-gray-900'>{cs.title}</h4>
                                  <Badge className={`${cs.difficulty === 'simple' ? 'difficulty-simple' : 'difficulty-complex'} border-0 text-xs`}>
                                    {cs.difficulty}
                                  </Badge>
                                </div>
                                <p className='text-sm text-gray-600 mb-2'>{cs.description}</p>
                                <div className='flex items-center space-x-4 text-xs text-gray-500'>
                                  <span>By {cs.author_name}</span>
                                  <span>👁 {cs.view_count} views</span>
                                  <span>👍 {cs.upvotes} upvotes</span>
                                  <span>{formatDate(cs.created_at)}</span>
                                </div>
                              </div>
                              <div className='flex flex-col space-y-2 ml-4'>
                                <Link to={`/case-study/${cs.id}`}>
                                  <Button
                                    variant='outline'
                                    size='sm'
                                    className='w-full rounded-full'
                                  >
                                    View
                                  </Button>
                                </Link>
                                <Button
                                  onClick={() => deleteCaseStudy(cs.id)}
                                  variant='outline'
                                  size='sm'
                                  className='w-full rounded-full text-red-600 border-red-300 hover:bg-red-50'
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Comments Moderation */}
                  <div className='mt-8'>
                    <h3 className='text-xl font-bold text-gray-900 mb-4'>Comments Moderation</h3>
                    <div className='text-sm text-gray-600 mb-4'>
                      Total: {allComments.length} comments | You can delete inappropriate comments
                    </div>
                    {allComments.length === 0 ? (
                      <div className='text-center py-8 text-gray-500'>No comments found</div>
                    ) : (
                      <div className='space-y-3'>
                        {allComments.map(comment => (
                          <div key={comment.id} className='bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-all'>
                            <div className='flex items-start justify-between'>
                              <div className='flex-1'>
                                <div className='flex items-center space-x-2 mb-2'>
                                  <span className='font-medium text-gray-900'>{comment.author_name}</span>
                                  <span className='text-xs text-gray-500'>{formatDate(comment.created_at)}</span>
                                </div>
                                <p className='text-sm text-gray-700 mb-2'>{comment.content}</p>
                                <div className='text-xs text-gray-500'>
                                  On case study: <Link to={`/case-study/${comment.case_study_id}`} className='text-blue-600 hover:underline'>View case</Link>
                                </div>
                              </div>
                              <Button
                                onClick={() => deleteComment(comment.id)}
                                variant='outline'
                                size='sm'
                                className='rounded-full text-red-600 border-red-300 hover:bg-red-50'
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Student;